import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { answerFor } from "../components/home/Hero";
import { Brand } from "../components/Shared";

const API = process.env.REACT_APP_BACKEND_URL;

export function sessionFor(mode) {
  const key = `vl-session-${mode}`;
  let id = localStorage.getItem(key);
  if (!id) { id = `${mode}-${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`; localStorage.setItem(key, id); }
  return id;
}

export function useAssistant(mode) {
  const [sessionId, setSessionId] = useState(() => sessionFor(mode));
  useEffect(() => { setSessionId(sessionFor(mode)); }, [mode]);
  const [busy, setBusy] = useState(false);
  const abortRef = useRef(null);
  const reset = useCallback(() => { abortRef.current?.abort(); localStorage.removeItem(`vl-session-${mode}`); const id = sessionFor(mode); setSessionId(id); setBusy(false); return id; }, [mode]);
  const adopt = useCallback(() => { setSessionId(sessionFor(mode)); }, [mode]);

  const send = useCallback(async (message, onDelta) => {
    abortRef.current?.abort();
    const ctrl = new AbortController(); abortRef.current = ctrl;
    setBusy(true);
    window.dispatchEvent(new CustomEvent("vl-chat-updated", { detail: { sessionId, message, pending: true } }));
    try {
      const r = await fetch(`${API}/api/assistant/chat`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ currency: (() => { try { return JSON.parse(localStorage.getItem("vl-locale") || "{}").currency || "USD"; } catch { return "USD"; } })(), session_id: sessionId, message, mode, is_client: false, persona: mode === "business" ? localStorage.getItem("vl-biz-persona") || "" : "" }), signal: ctrl.signal });
      if (!r.ok || !r.body) throw new Error("bad response");
      const reader = r.body.getReader(); const dec = new TextDecoder(); let buf = "", done = null;
      while (true) {
        const { value, done: end } = await reader.read();
        if (end) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const ev = JSON.parse(p.slice(6));
          if (ev.type === "delta") onDelta?.(ev.text);
          else if (ev.type === "done") { done = ev; window.dispatchEvent(new CustomEvent("vl-chat-updated")); }
          else if (ev.type === "error") throw new Error(ev.text);
        }
      }
      if (!done) throw new Error("no answer");
      return { text: done.text, scope: done.scope || "library", citations: done.citations || [], followups: done.followups || [], cta: done.cta || null, chart: done.chart || null, stats: done.stats || [], live: true };
    } catch (e) {
      if (e.name === "AbortError") return null;
      const a = answerFor(message, mode === "business");
      return { text: a.answer, citations: [{ label: a.source, url: mode === "business" ? "/business/pricing" : "/eligibility" }], followups: [], cta: { label: mode === "business" ? "Book a demo" : "Check my eligibility", to: mode === "business" ? "/contact" : "/eligibility" }, live: false };
    } finally { setBusy(false); }
  }, [sessionId, mode]);

  return { send, busy, sessionId, reset, adopt };
}

export function Rich({ text, className = "" }) {
  const lines = String(text || "").replace(/\s*—\s*/g, ", ").split("\n");
  const render = (line, i) => {
    const parts = line.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\)|Vistolane)/g).filter(Boolean).map((p, j) => {
      if (p === "Vistolane") return <Brand key={`b-${j}`} />;
      const b = p.match(/^\*\*([^*]+)\*\*$/); if (b) return <strong key={`s-${j}-${b[1]}`}>{b[1]}</strong>;
      const l = p.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (l) return l[2].startsWith("/") ? <Link key={`l-${l[2]}`} to={l[2]} className="font-semibold text-tint-strong underline underline-offset-2">{l[1]}</Link> : <a key={`a-${l[2]}`} href={l[2]} target="_blank" rel="noopener noreferrer" className="font-semibold text-tint-strong underline underline-offset-2">{l[1]}</a>;
      return p;
    });
    const bullet = /^\s*[-•*]\s+/.test(line);
    return <p key={i} className={bullet ? "pl-4 -indent-3" : line.trim() ? "" : "h-2"}>{bullet ? "• " : ""}{bullet ? parts.map((p) => (typeof p === "string" ? p.replace(/^\s*[-•*]\s+/, "") : p)) : parts}</p>;
  };
  return <div className={`space-y-1.5 ${className}`}>{lines.map(render)}</div>;
}

export function Citations({ items, light, scope }) {
  if (!items?.length && scope !== "web") return null;
  return (
    <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="citations">
      {scope === "web" && <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${light ? "bg-amber-300/25 text-amber-100" : "bg-amber-50 text-amber-800 border border-amber-200"}`} title="Answered from official published sources outside the Vistolane library. Verify the current figure on the linked page." data-testid="citation-scope-web">Outside our library · from official sources</span>}
      {scope === "library" && <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${light ? "bg-white/20 text-white" : "bg-success/10 text-success-strong"}`} data-testid="citation-scope-library">Vistolane library</span>}
      {items.map((c) => {
        const cls = `inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${light ? "bg-white/15 text-white" : "bg-background border border-separator text-label2 hover:text-foreground"}`;
        const key = c.url || c.label;
        return c.url?.startsWith("/") ? <Link key={key} to={c.url} className={cls}>{c.label}</Link> : <a key={key} href={c.url} target="_blank" rel="noopener noreferrer" className={cls}>{c.label} ↗</a>;
      })}
    </div>
  );
}

export function DataViz({ chart, stats, light, compact = false }) {
  const bars = chart?.series?.filter((b) => typeof b.value === "number" && isFinite(b.value)) || [];
  const hasStats = Array.isArray(stats) && stats.length > 0;
  if (!bars.length && !hasStats) return null;
  const max = Math.max(...bars.map((b) => b.value), 1);
  const fmt = (v) => (chart?.unit === "$" ? `$${v.toLocaleString()}` : `${v.toLocaleString()}${chart?.unit && chart.unit !== "$" ? ` ${chart.unit}` : ""}`);
  return (
    <div className={`mt-4 space-y-3 ${compact ? "text-xs" : "text-sm"}`} data-testid="answer-dataviz">
      {hasStats && (
        <div className={`grid gap-2 ${compact ? "grid-cols-2" : "grid-cols-2 sm:grid-cols-4"}`} data-testid="answer-stats">
          {stats.slice(0, 4).map((st) => <div key={st.label} className={`rounded-xl px-3 py-2 ${light ? "bg-white/10" : "bg-gray6"}`}><p className={`text-xs ${light ? "text-on-dark-muted" : "text-label2"}`}>{st.label}</p><p className={`font-semibold ${compact ? "text-sm" : "text-base"}`}>{st.value}</p></div>)}
        </div>
      )}
      {bars.length >= 2 && (
        <div className={`rounded-card border p-3 ${light ? "border-white/15" : "border-separator"}`} data-testid="answer-chart">
          {chart.title && <p className="mb-2 text-xs font-semibold">{chart.title}</p>}
          <ul className="space-y-1.5">
            {bars.slice(0, 6).map((b) => <li key={b.label} className="grid grid-cols-[minmax(0,7rem)_1fr_auto] items-center gap-2"><span className={`truncate text-xs ${light ? "text-on-dark" : "text-label2"}`} title={b.label}>{b.label}</span><span className={`h-2 overflow-hidden rounded-full ${light ? "bg-white/15" : "bg-gray5"}`}><span className="block h-full rounded-full bg-tint" style={{ width: `${Math.max(3, Math.round((b.value / max) * 100))}%` }} /></span><span className="text-xs font-semibold tabular-nums">{fmt(b.value)}</span></li>)}
          </ul>
        </div>
      )}
    </div>
  );
}
